import React, { useRef, useState, useEffect, useCallback } from 'react';
import { CardSchema } from 'constants/graphqlTypes';
import { BreathingWrap } from './styled';
import Body from '../Common/Body';

// Debug helper (opt-in only when window.__ACV_DEBUG === true)
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-underscore-dangle */
const acvLog = (...args: any[]) => {
  try {
    if ((window as any).__ACV_DEBUG !== true) return;
    console.log('[ACV]', ...args);
  } catch (e) {
    // noop
  }
};
/* eslint-enable no-console */

interface AnimatedCardVisualProps {
  card: CardSchema;
  dataTestId?: string;
  colorValue: string;
  pauseAnimation?: boolean;
  disableAnimation?: boolean;
  intersectionThreshold?: number;
  intersectionRootMargin?: string;
}

const AnimatedCardVisual: React.FC<AnimatedCardVisualProps> = React.memo(
  ({
    card,
    dataTestId,
    colorValue,
    pauseAnimation,
    disableAnimation = false,
    intersectionThreshold = 0.7,
    intersectionRootMargin = '10% 0px -10% 0px',
  }) => {
    const [isBreathing, setIsBreathing] = useState(false);
    const [isMoving, setIsMoving] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [isReturning, setIsReturning] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const [cardSize, setCardSize] = useState({ width: 0, height: 0 });
    const parentRef = useRef<HTMLDivElement>(null);
    const [breathingPhase, setBreathingPhase] = useState<'inhale' | 'hold1' | 'exhale' | 'hold2'>('inhale');
    const breathingTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const animationTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const startSequenceRef = useRef<() => void>(() => {});
    const instanceIdRef = useRef<string>(`acv-${Math.random().toString(36).slice(2)}`);

    const hasStartedRef = useRef(false);
    const isScrollingRef = useRef(false);
    const scrollIdleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const SCROLL_IDLE_MS = 150;
    const pendingStartRef = useRef(false);
    const isRunningRef = useRef(false);
    const isActiveRef = useRef(false);

    // Entrance-from-bottom animation state
    const [presetEntranceFromBottom, setPresetEntranceFromBottom] = useState(false);
    const [isEnteringFromBottom, setIsEnteringFromBottom] = useState(false);
    const entranceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Scroll-tethered animation state
    const [isImageAnimating, setIsImageAnimating] = useState(false);
    const [imageAnimationProgress, setImageAnimationProgress] = useState(0);
    const imageRef = useRef<HTMLDivElement>(null);
    const lastScrollY = useRef(0);
    const isInTriggerZone = useRef(false);

    // iOS-style keyframe interpolation function
    const interpolateFromKeyframes = (progress: number, keyframes: number[]): number => {
      const kfProgress = [0.0, 0.25, 0.5, 0.6, 0.75, 1.0];
      let lowerIndex = 0;
      let upperIndex = 1;
      for (let i = 0; i < kfProgress.length - 1; i += 1) {
        if (progress >= kfProgress[i] && progress <= kfProgress[i + 1]) {
          lowerIndex = i;
          upperIndex = i + 1;
          break;
        }
      }
      if (progress > kfProgress[kfProgress.length - 1]) {
        return keyframes[keyframes.length - 1];
      }
      const lowerProgress = kfProgress[lowerIndex];
      const upperProgress = kfProgress[upperIndex];
      const lowerValue = keyframes[lowerIndex];
      const upperValue = keyframes[upperIndex];
      const t = (progress - lowerProgress) / (upperProgress - lowerProgress);
      return lowerValue + (upperValue - lowerValue) * t;
    };

    useEffect(() => {
      acvLog(instanceIdRef.current, 'mount', { threshold: intersectionThreshold, rootMargin: intersectionRootMargin });
      const handleResize = () => {
        if (!parentRef.current) return;
        const rect = parentRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setCardSize({ width: rect.width, height: rect.height });
          acvLog(instanceIdRef.current, 'measured', rect.width, rect.height);
        }
      };
      handleResize();
      if (!parentRef.current) return undefined;
      const resizeObserver = new window.ResizeObserver(handleResize);
      resizeObserver.observe(parentRef.current);
      return () => resizeObserver.disconnect();
    }, [intersectionRootMargin, intersectionThreshold]);

    // Observe card entrance from bottom: preset starting state below viewport, then animate to final
    useEffect(() => {
      if (disableAnimation) return undefined;
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const viewportHeight = window.innerHeight;
          const elementTop = entry.boundingClientRect.top;
          const isBelowViewport = elementTop > viewportHeight;
          if (!entry.isIntersecting && isBelowViewport) {
            setPresetEntranceFromBottom(true);
          } else if (entry.isIntersecting && presetEntranceFromBottom) {
            setIsEnteringFromBottom(true);
            setPresetEntranceFromBottom(false);
            if (entranceTimeoutRef.current) clearTimeout(entranceTimeoutRef.current);
            entranceTimeoutRef.current = setTimeout(() => {
              setIsEnteringFromBottom(false);
            }, 800);
          }
        },
        { threshold: [0], rootMargin: '0px' }
      );
      if (cardRef.current) observer.observe(cardRef.current);
      return () => {
        observer.disconnect();
        if (entranceTimeoutRef.current) clearTimeout(entranceTimeoutRef.current);
      };
    }, [disableAnimation, presetEntranceFromBottom]);

    const clearBreathingTimeouts = () => {
      breathingTimeoutsRef.current.forEach(clearTimeout);
      breathingTimeoutsRef.current = [];
    };

    const clearAnimationTimeouts = () => {
      animationTimeoutsRef.current.forEach(clearTimeout);
      animationTimeoutsRef.current = [];
    };

    const startBreathingCycle = useCallback((onComplete?: () => void): void => {
      clearBreathingTimeouts();
      // Stage 1: enable breathing with baseline transform and transition available
      setIsBreathing(true);
      setBreathingPhase('inhale');
      acvLog(instanceIdRef.current, 'breathing:arm');
      // Stage 2: next frame, force reflow and switch to exhale so transition applies
      requestAnimationFrame(() => {
        try {
          const el = cardRef.current;
          if (el) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            el.offsetHeight;
          }
          // eslint-disable-next-line no-empty
        } catch (_) {}
        setBreathingPhase('exhale');
        acvLog(instanceIdRef.current, 'breathing:start');
        breathingTimeoutsRef.current.push(
          setTimeout(() => {
            setBreathingPhase('hold2');
            breathingTimeoutsRef.current.push(
              setTimeout(() => {
                setBreathingPhase('inhale');
                breathingTimeoutsRef.current.push(
                  setTimeout(() => {
                    setBreathingPhase('hold1');
                    breathingTimeoutsRef.current.push(
                      setTimeout(() => {
                        setIsBreathing(false);
                        if (onComplete) onComplete();
                      }, 0)
                    );
                  }, 1650)
                );
              }, 500)
            );
          }, 2250)
        );
      });
    }, []);

    const runSequenceCycle = useCallback((): void => {
      // if stopped mid-cycle, don't continue
      if (!isRunningRef.current) return;
      clearAnimationTimeouts();
      animationTimeoutsRef.current.push(
        setTimeout(() => {
          if (!isRunningRef.current || pauseAnimation) return;
          acvLog(instanceIdRef.current, 'sequence:start');
          isActiveRef.current = true;
          // Ensure entrance animation does not override breathing transforms
          setIsEnteringFromBottom(false);
          setPresetEntranceFromBottom(false);
          startBreathingCycle(() => {
            if (!isRunningRef.current || pauseAnimation) return;
            setIsMoving(true);
            animationTimeoutsRef.current.push(
              setTimeout(() => {
                if (!isRunningRef.current || pauseAnimation) return;
                setIsMoving(false);
                setIsRotating(true);
                animationTimeoutsRef.current.push(
                  setTimeout(() => {
                    if (!isRunningRef.current || pauseAnimation) return;
                    setIsRotating(false);
                    setIsReturning(true);
                    animationTimeoutsRef.current.push(
                      setTimeout(() => {
                        if (!isRunningRef.current || pauseAnimation) return;
                        setIsMoving(false);
                        setIsReturning(false);
                        // Loop next cycle if still running and not paused
                        if (isRunningRef.current && !pauseAnimation) {
                          runSequenceCycle();
                        }
                      }, 500)
                    );
                  }, 1500)
                );
              }, 500)
            );
          });
        }, 1000)
      );
    }, [pauseAnimation, startBreathingCycle]);

    const startAnimationSequence = useCallback((): void => {
      if (disableAnimation || pauseAnimation) {
        acvLog(instanceIdRef.current, 'sequence:blocked:paused=true');
        return;
      }
      if (isRunningRef.current) {
        acvLog(instanceIdRef.current, 'sequence:already-running');
        return;
      }
      isRunningRef.current = true;
      hasStartedRef.current = true;
      acvLog(instanceIdRef.current, 'sequence:prestart');
      runSequenceCycle();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableAnimation, pauseAnimation, runSequenceCycle]);

    useEffect(() => {
      startSequenceRef.current = startAnimationSequence;
    }, [startAnimationSequence, pauseAnimation]);

    const stopAnimation = useCallback((): void => {
      acvLog(instanceIdRef.current, 'sequence:stop');
      setIsBreathing(false);
      setIsMoving(false);
      setIsRotating(false);
      setIsReturning(false);
      setBreathingPhase('inhale');
      clearBreathingTimeouts();
      clearAnimationTimeouts();
      isRunningRef.current = false;
      hasStartedRef.current = false;
      isActiveRef.current = false;
    }, []);

    // Claim/release active based on 70% ratio using global arbiter
    useEffect(() => {
      if (disableAnimation || !cardRef.current) return undefined;

      // Bind to viewport explicitly
      const observer = new IntersectionObserver(
        () => {
          const el = cardRef.current;
          if (!el) return;
          const r = el.getBoundingClientRect();
          const rr = { top: 0, bottom: window.innerHeight } as { top: number; bottom: number };
          const top = Math.max(r.top, rr.top);
          const bottom = Math.min(r.bottom, rr.bottom);
          const visible = Math.max(0, bottom - top);
          const ratio = r.height > 0 ? visible / r.height : 0;
          acvLog(instanceIdRef.current, 'observer:tick', { ratio: Number(ratio.toFixed(3)) });

          // Direct start/stop based on ratio against viewport
          if (ratio >= intersectionThreshold) {
            if (!isScrollingRef.current && !pauseAnimation && !isRunningRef.current) {
              hasStartedRef.current = true;
              startSequenceRef.current();
            } else {
              acvLog(instanceIdRef.current, 'sequence:gate', {
                isScrolling: isScrollingRef.current,
                paused: !!pauseAnimation,
                hasStarted: hasStartedRef.current,
                running: isRunningRef.current,
                ratio: Number(ratio.toFixed(3)),
              });
              if (isScrollingRef.current) pendingStartRef.current = true;
            }
            // Fallback: if we still didn't start, try shortly after
            if (!pauseAnimation && !isScrollingRef.current) {
              setTimeout(() => {
                if (!isRunningRef.current) {
                  hasStartedRef.current = true;
                  acvLog(instanceIdRef.current, 'sequence:start:fallback');
                  startSequenceRef.current();
                }
              }, 50);
            }
          } else {
            if (isRunningRef.current || hasStartedRef.current) {
              acvLog(instanceIdRef.current, 'sequence:stop');
            }
            stopAnimation();
            pendingStartRef.current = false;
          }
        },
        { threshold: [0, intersectionThreshold], root: undefined, rootMargin: '0px' }
      );
      observer.observe(cardRef.current);
      return () => observer.disconnect();
    }, [disableAnimation, intersectionThreshold, pauseAnimation, stopAnimation]);

    // Stop animation immediately on scroll; restart only after scrolling is idle
    useEffect(() => {
      if (disableAnimation) return undefined;
      const onScroll = () => {
        isScrollingRef.current = true;
        // Only stop immediately if an animation is actively running
        if (isActiveRef.current) {
          acvLog(instanceIdRef.current, 'scroll:stop');
          stopAnimation();
        } else {
          acvLog(instanceIdRef.current, 'scroll:stop');
        }
        if (scrollIdleTimeoutRef.current) clearTimeout(scrollIdleTimeoutRef.current);
        scrollIdleTimeoutRef.current = setTimeout(() => {
          isScrollingRef.current = false;
          acvLog(instanceIdRef.current, 'scroll:idle');

          const el = cardRef.current;
          if (el) {
            // Compute visibility against viewport
            const r = el.getBoundingClientRect();
            const rr = { top: 0, bottom: window.innerHeight } as { top: number; bottom: number };
            const top = Math.max(r.top, rr.top);
            const bottom = Math.min(r.bottom, rr.bottom);
            const visible = Math.max(0, bottom - top);
            const ratio = r.height > 0 ? visible / r.height : 0;
            acvLog(instanceIdRef.current, 'idle:ratio', Number(ratio.toFixed(3)));
            if (ratio >= intersectionThreshold) {
              if (!pauseAnimation && (pendingStartRef.current || !isRunningRef.current)) {
                hasStartedRef.current = true;
                startSequenceRef.current();
                pendingStartRef.current = false;
              } else {
                acvLog(instanceIdRef.current, 'sequence:gate', {
                  paused: !!pauseAnimation,
                  pending: pendingStartRef.current,
                  hasStarted: hasStartedRef.current,
                  running: isRunningRef.current,
                  isScrolling: isScrollingRef.current,
                  ratio: Number(ratio.toFixed(3)),
                });
              }
              // Fallback: ensure start even if timing/state blocked immediate start
              if (!pauseAnimation) {
                setTimeout(() => {
                  if (!isRunningRef.current && !isScrollingRef.current) {
                    hasStartedRef.current = true;
                    acvLog(instanceIdRef.current, 'sequence:start:fallback');
                    startSequenceRef.current();
                    pendingStartRef.current = false;
                  }
                }, 50);
              }
            } else {
              acvLog(instanceIdRef.current, 'sequence:stop');
              stopAnimation();
              pendingStartRef.current = false;
            }
          }
        }, SCROLL_IDLE_MS);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', onScroll);
        if (scrollIdleTimeoutRef.current) clearTimeout(scrollIdleTimeoutRef.current);
      };
    }, [disableAnimation, pauseAnimation, stopAnimation, intersectionThreshold]);

    useEffect(() => {
      if (disableAnimation) return undefined;
      const handleScroll = (): void => {
        if (!imageRef.current || !isInTriggerZone.current) return;
        const currentScrollY = window.scrollY;
        lastScrollY.current = currentScrollY;
        const rect = imageRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const triggerStartY = viewportHeight * 0.25;
        const animationEndYPos = viewportHeight * 0.01;
        const currentY = rect.bottom;
        const animationRange = triggerStartY - animationEndYPos;
        const progressInRange = (triggerStartY - currentY) / animationRange;
        const clampedProgress = Math.max(0, Math.min(1, progressInRange));
        setImageAnimationProgress(clampedProgress);
        setIsImageAnimating(clampedProgress > 0 && clampedProgress < 1);
      };
      const imageObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const { intersectionRatio, boundingClientRect } = entry;
          const viewportHeight = window.innerHeight;
          const triggerStartY = viewportHeight * 0.25;
          isInTriggerZone.current = intersectionRatio > 0 && boundingClientRect.bottom <= triggerStartY;
          if (isInTriggerZone.current) {
            lastScrollY.current = window.scrollY;
            handleScroll();
          } else {
            setIsImageAnimating(false);
            setImageAnimationProgress(0);
          }
        },
        {
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
          rootMargin: '25% 0px -6% 0px',
        }
      );
      if (imageRef.current) {
        imageObserver.observe(imageRef.current);
      }
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        imageObserver.disconnect();
        window.removeEventListener('scroll', handleScroll);
      };
    }, [disableAnimation]);

    const interpolatedRotation = interpolateFromKeyframes(imageAnimationProgress, [
      -0.0,
      -8.54,
      -25.35,
      -59.88,
      -109.1,
      -120.0,
    ]);
    const interpolatedScale = interpolateFromKeyframes(imageAnimationProgress, [1.0, 0.7, 0.52, 0.44, 0.3, 0.0]);

    return (
      <div ref={parentRef} style={{ perspective: 2000 }}>
        <BreathingWrap
          ref={cardRef}
          isBreathing={isBreathing}
          isMoving={isMoving}
          isRotating={isRotating}
          isReturning={isReturning}
          cardWidth={cardSize.width}
          cardHeight={cardSize.height}
          breathingPhase={breathingPhase}
          bgColor={colorValue}
          presetEntranceFromBottom={presetEntranceFromBottom}
          isEnteringFromBottom={isEnteringFromBottom}
        >
          <Body
            dataTestId={dataTestId}
            card={card}
            isFirstUp
            imageRef={imageRef}
            imageAnimationProgress={imageAnimationProgress}
            isImageAnimating={isImageAnimating}
            interpolatedRotation={interpolatedRotation}
            interpolatedScale={interpolatedScale}
            onImageLoad={() => {
              if (parentRef.current) {
                const rect = parentRef.current.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                  setCardSize({ width: rect.width, height: rect.height });
                  acvLog(instanceIdRef.current, 'imageLoad:measured', rect.width, rect.height);
                }
              }
            }}
          />
        </BreathingWrap>
      </div>
    );
  }
);

export default AnimatedCardVisual;
